import type { DragEndEvent } from '@dnd-kit/core';
import {
	DndContext,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	rectSortingStrategy,
	sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Box, Group, Stack, createStyles } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import { type IMetricWidget } from '../../api';
import { EmptyState } from '../../components/EmptyState';
import { appShellStore } from '../../components/SecodaAppShell/store';
import { useCheckAnalyticsWritePermission } from '../../utils/authorization/roles';
import { useReportManagement } from '../HomePage/hooks/useReportManagement';
import AddWidgetModal from './components/AddWidgetModal/AddWidgetModal';
import AddWidgetButton from './components/Buttons/AddWidgetButton';
import { MetricWidget } from './components/MetricWidget';

const useStyles = createStyles((theme) => ({
	// Empty state
	root: {
		flexGrow: 1,
		height: '100%',
		paddingBottom: theme.spacing.sm,
	},
	emptyStateContainer: {
		flexGrow: 1,
		display: 'flex',
	},
	wrapper: {
		width: '100%',
		display: 'grid',
		gridTemplateRows: '1fr',
		gridTemplateColumns: 'repeat(12, 1fr)',
		gap: theme.spacing.md,
	},
}));

interface DynamicReportProps {
	id: string;
}

export const DynamicReport = observer(({ id }: DynamicReportProps) => {
	const { classes } = useStyles();

	const {
		analyticsPageAddWidgetModalHandler: { close },
		navBarUI: {
			analyticsPage: { addWidgetModalOpen },
		},
	} = appShellStore;

	// Widgets logic
	const {
		report,
		updateWidget,
		removeWidget,
		reorderWidgets,
		addWidget,
		widgets,
	} = useReportManagement({
		type: 'analytics',
		id,
	});

	// Before, this line was `const readOnly = isDefaultReport(report)`
	// I've made it possible to edit default reports as part of ENG-17910
	// To work correctly, we must also check that the user has correct IAM permissions and owns the widget (this was not correct before)
	const readOnly = !useCheckAnalyticsWritePermission({ report });

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const handleDragEnd = async ({ active, over }: DragEndEvent) => {
		if (!over) {
			return;
		}

		const from = widgets.findIndex((info) => info.id === active.id);
		const to = widgets.findIndex((info) => info.id === over.id);

		reorderWidgets({ from, to });
	};

	if (widgets.length === 0 && !readOnly) {
		return (
			<>
				<AddWidgetModal
					addWidget={addWidget}
					opened={addWidgetModalOpen}
					onClose={close}
				/>
				<Stack className={classes.root}>
					<Box className={classes.emptyStateContainer}>
						<EmptyState
							illustrationName="reports"
							title="Add widgets to this report"
							description="Get started with a template or by adding widgets from the widget gallery."
							includeGoBack={false}
							size="lg"
							descWidth="100%"
							width="100%"
							withActions={
								<Group>
									<AddWidgetButton />
								</Group>
							}
						/>
					</Box>
				</Stack>
			</>
		);
	}

	return (
		<>
			{!readOnly && (
				<AddWidgetModal
					addWidget={addWidget}
					opened={addWidgetModalOpen}
					onClose={close}
				/>
			)}
			<Box className={classes.wrapper}>
				<DndContext
					collisionDetection={closestCenter}
					sensors={sensors}
					onDragEnd={handleDragEnd}
				>
					<SortableContext strategy={rectSortingStrategy} items={widgets}>
						{widgets.map((widget) => (
							<MetricWidget
								key={widget.id}
								metricWidget={widget as IMetricWidget}
								updateWidget={updateWidget}
								deleteWidget={removeWidget(widget.id)}
								readOnly={readOnly}
							/>
						))}
					</SortableContext>
				</DndContext>
			</Box>
		</>
	);
});
